import dayjs from 'dayjs';
import { formatTimestamp, formatDateOnly } from './timestamps';

// runDisplay.js
// One display vocabulary for Function runs, shared by the Dashboard function
// home (run picker + Activity feed) and the View widget provenance lines.
//
// A run has two independent axes:
//   - ORIGIN  (`trigger_type`): who started it — manual, view, agent, on_new_data,
//     on_schedule. An unknown origin is rendered verbatim, never silently as
//     "Manual".
//   - OUTCOME (`outcome_status`): did the stages and destinations achieve their
//     work. It is distinct from the compute `status`; legacy runs without
//     `outcome_status` fall back to the compute lifecycle so history still reads
//     sensibly.
//
// Everything here is pure. Icon components live in
// `components/common/RunChrome.js` so utils stay free of React.

export const RUN_ORIGINS = ['manual', 'view', 'agent', 'on_new_data', 'on_schedule'];

// Short label used inside chips and the run picker button.
export const RUN_ORIGIN_LABELS = {
  manual: 'Manual',
  view: 'View',
  agent: 'Agent',
  on_new_data: 'Data',
  on_schedule: 'Schedule',
};

// Longer label used where the trigger family matters (run detail header).
export const RUN_ORIGIN_DETAIL_LABELS = {
  manual: 'Manual',
  view: 'View',
  agent: 'Agent',
  on_new_data: 'Triggered · Data',
  on_schedule: 'Triggered · Schedule',
};

export const runOriginLabel = (triggerType) => (
  RUN_ORIGIN_LABELS[triggerType] || triggerType || 'Unknown'
);

export const runOriginDetailLabel = (triggerType) => (
  RUN_ORIGIN_DETAIL_LABELS[triggerType] || triggerType || 'Unknown'
);

// `outcome_status` answers "did the run achieve its work?"; `status` is the
// compute lifecycle. Runs written before outcome_status existed fall back.
export const deriveRunOutcome = (run) => {
  if (run?.outcome_status) return run.outcome_status;
  if (run?.status === 'completed') return 'succeeded';
  if (run?.status === 'failed') return 'failed';
  if (run?.status === 'running') return 'running';
  if (run?.status === 'queued') return 'queued';
  return 'unknown';
};

// A pending / warning / partial run is NOT an error — each has its own visual
// state. `glyph` is the compact mark used in the run picker and Activity rows.
export const OUTCOME_DISPLAY = {
  succeeded: { label: 'Succeeded', chipColor: 'success', glyph: '✓', tone: 'success' },
  warning: { label: 'Warning', chipColor: 'warning', glyph: '⚠', tone: 'warning' },
  partial_success: { label: 'Partial success', chipColor: 'warning', glyph: '⚠', tone: 'warning' },
  pending: { label: 'Pending', chipColor: 'info', glyph: '⋯', tone: 'info' },
  failed: { label: 'Failed', chipColor: 'error', glyph: '✕', tone: 'error' },
  running: { label: 'Running', chipColor: 'default', glyph: '⋯', tone: 'default', spinner: true },
  queued: { label: 'Queued', chipColor: 'default', glyph: '⋯', tone: 'default', spinner: true },
  unknown: { label: 'Unknown', chipColor: 'default', glyph: '?', tone: 'default' },
};

// Compute completion and successful outcome have the same display when no
// output outcome has been supplied. Keep this translation shared by callers.
export const outcomeDisplay = (outcome) => (
  OUTCOME_DISPLAY[outcome === 'completed' ? 'succeeded' : outcome] || OUTCOME_DISPLAY.unknown
);

// Sidebar status dot colours (mock palette). `null` means "draw no dot".
export const OUTCOME_DOT_COLOR = {
  succeeded: '#2EB998',
  warning: '#F0B25A',
  partial_success: '#F0B25A',
  failed: '#E84A5F',
  pending: '#6C757D',
  running: '#6C757D',
  queued: '#6C757D',
  unknown: '#6C757D',
};

export const outcomeDotColor = (outcome) => OUTCOME_DOT_COLOR[outcome] || null;

// The Activity filter groups. `partial` covers both partial_success and warning
// because a user reading the feed treats them as the same "went through but not
// cleanly" bucket.
export const OUTCOME_FILTERS = [
  { value: 'all', label: 'All outcomes' },
  { value: 'succeeded', label: 'Succeeded', matches: ['succeeded'] },
  { value: 'partial', label: 'Partial', matches: ['partial_success', 'warning'] },
  { value: 'failed', label: 'Failed', matches: ['failed'] },
];

export const ORIGIN_FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'manual', label: 'Manual' },
  { value: 'view', label: 'View' },
  { value: 'agent', label: 'Agent' },
  { value: 'on_new_data', label: 'Data' },
  { value: 'on_schedule', label: 'Schedule' },
];

export const matchesOutcomeFilter = (run, filterValue) => {
  if (!filterValue || filterValue === 'all') return true;
  const filter = OUTCOME_FILTERS.find((entry) => entry.value === filterValue);
  if (!filter?.matches) return true;
  return filter.matches.includes(deriveRunOutcome(run));
};

export const matchesOriginFilter = (run, filterValue) => (
  !filterValue || filterValue === 'all' || run?.trigger_type === filterValue
);

// --- Timestamps -------------------------------------------------------------

// Firestore Timestamps, ISO strings and epoch numbers all appear on run docs
// depending on which writer created them.
export const toDate = (value) => {
  if (!value) return null;
  if (value.toDate) {
    try { return value.toDate(); } catch (err) { return null; }
  }
  if (value instanceof Date) return value;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

export const runCreatedAt = (run) => toDate(run?.created_at);
export const runCompletedAt = (run) => toDate(run?.completed_at) || runCreatedAt(run);

// Fixed 24-hour clock (see utils/timestamps): the browser default flips between
// "08:15 PM" and "20:15" from machine to machine.
const HHMM = (date) => dayjs(date).format('HH:mm');

const sameDay = (a, b) => (
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()
);

// Short, relative-ish stamp: "just now", "14m ago", "Today 07:00", "Yest 16:42",
// "Tue 09:14", then the product-wide "12 Jul 26, 15:40" once a run is over a
// week old. Always the fixed 24-hour clock.
export const formatRunTime = (value) => {
  const date = toDate(value);
  if (!date) return '';
  const now = new Date();
  const deltaMs = now.getTime() - date.getTime();

  if (deltaMs >= 0 && deltaMs < 60 * 1000) return 'just now';
  if (deltaMs >= 0 && deltaMs < 60 * 60 * 1000) return `${Math.floor(deltaMs / 60000)}m ago`;
  if (sameDay(date, now)) return `Today ${HHMM(date)}`;

  const yesterday = new Date(now.getTime());
  yesterday.setDate(now.getDate() - 1);
  if (sameDay(date, yesterday)) return `Yest ${HHMM(date)}`;

  if (deltaMs >= 0 && deltaMs < 7 * 24 * 60 * 60 * 1000) {
    return dayjs(date).format('ddd HH:mm');
  }
  return formatTimestamp(date);
};

// The even shorter form used in the function sidebar (no clock when it is old).
export const formatRunTimeShort = (value) => {
  const date = toDate(value);
  if (!date) return '';
  const now = new Date();
  const deltaMs = now.getTime() - date.getTime();
  if (deltaMs >= 0 && deltaMs < 60 * 1000) return 'now';
  if (deltaMs >= 0 && deltaMs < 60 * 60 * 1000) return `${Math.floor(deltaMs / 60000)}m`;
  if (sameDay(date, now)) return HHMM(date);
  const yesterday = new Date(now.getTime());
  yesterday.setDate(now.getDate() - 1);
  if (sameDay(date, yesterday)) return 'Yest';
  if (deltaMs >= 0 && deltaMs < 7 * 24 * 60 * 60 * 1000) {
    return dayjs(date).format('ddd');
  }
  return formatDateOnly(date);
};

// --- Run parameters ---------------------------------------------------------

const isPlainObject = (value) => value !== null && typeof value === 'object' && !Array.isArray(value);

const formatParameterValue = (value) => {
  if (value === null || value === undefined || value === '') return null;
  if (Array.isArray(value)) return value.length > 0 ? value.join(', ') : null;
  if (isPlainObject(value)) {
    const gte = value.gte || value.from || '';
    const lte = value.lte || value.to || '';
    if (gte || lte) return `${gte || '…'} → ${lte || '…'}`;
    return null;
  }
  return String(value);
};

// Flatten a run's stored `parameters` map (keyed by data-source block id, then
// by filter name, with `exclude_filters` / `range_filters` sub-maps) into simple
// "Column: value" lines for a provenance tooltip.
export const describeRunParameters = (run) => {
  const parameters = isPlainObject(run?.parameters) ? run.parameters : {};
  const lines = [];
  Object.values(parameters).forEach((block) => {
    if (!isPlainObject(block)) return;
    Object.entries(block).forEach(([key, value]) => {
      if (key === 'exclude_filters' && isPlainObject(value)) {
        Object.entries(value).forEach(([column, excluded]) => {
          const display = formatParameterValue(excluded);
          if (display) lines.push(`${column.replace(/_/g, ' ')} is not ${display}`);
        });
        return;
      }
      if (key === 'range_filters' && isPlainObject(value)) {
        Object.entries(value).forEach(([column, range]) => {
          const display = formatParameterValue(range);
          if (display) lines.push(`${column.replace(/_/g, ' ')}: ${display}`);
        });
        return;
      }
      const display = formatParameterValue(value);
      if (display) lines.push(`${key.replace(/_/g, ' ')}: ${display}`);
    });
  });
  return lines;
};

// "20260829-1412": the run's start time as a filename stamp, or null when the
// run (or its time) is not known.
export const runExportStamp = (run) => {
  const date = runCreatedAt(run);
  if (!date) return null;
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}${pad(date.getMonth() + 1)}${pad(date.getDate())}-${pad(date.getHours())}${pad(date.getMinutes())}`;
};
